import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const COUNTRIES_GEOJSON_URL = "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson"

export default class extends Controller {
  static values = {
    articles: Array,
    countryCodes: Array,
    activeCountry: String,
    pinPath: String,
    countryNames: Object,
    countrySlugs: Object
  }


  initialize() {
    this.highlightCountry = this.highlightCountry.bind(this)
    this.resetCountryHighlight = this.resetCountryHighlight.bind(this)
    this.onCountryClick = this.onCountryClick.bind(this)
    this.markers = []
  }

  connect() {
    this.element.setAttribute('style', 'height: 50vh; width: 100%; position: relative; background-color: #dddddd;')
    this.countryCodes = this.countryCodesValue || []
    this.articles = this.articlesValue || []
    this.activeCountry = this.activeCountryValue || null
    this.pinPath = this.pinPathValue
    this.countryNames = this.countryNamesValue || {}
    this.countrySlugs = this.countrySlugsValue || {}

    this.map = L.map(this.element, { minZoom: 3 }).setView([20, 0], 3)

    if (this.activeCountry) {
      this.showResetButton()
    }

    this.addCountryHighlights()
  }

  getCountrySlug(countryCode) {
    return this.countrySlugs[countryCode] || countryCode.toLowerCase()
  }

  addTileLayer() {
    if (!this.tileLayer) {
      this.tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map)
      this.tileLayer.bringToBack()
    }
  }

  buildOceanMask(activeFeatures) {
    if (!activeFeatures || activeFeatures.length === 0) return null
    const worldRing = [[-90, -180], [-90, 180], [90, 180], [90, -180]]
    const holes = []
    activeFeatures.forEach(feature => {
      const geom = feature.geometry
      if (!geom) return
      if (geom.type === 'Polygon') {
        holes.push(geom.coordinates[0].map(([lng, lat]) => [lat, lng]))
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach(polygon => {
          holes.push(polygon[0].map(([lng, lat]) => [lat, lng]))
        })
      }
    })
    if (holes.length === 0) return null
    return L.polygon([worldRing, ...holes], {
      stroke: false,
      fillColor: '#dddddd',
      fillOpacity: 1,
      interactive: false,
      renderer: L.svg({ padding: 5 }),
    })
  }

  async addCountryHighlights() {
    try {
      const response = await fetch(COUNTRIES_GEOJSON_URL)
      if (!response.ok) throw new Error(`Country dataset request failed: ${response.status}`)

      const countries = await response.json()

      if (this.activeCountry) {
        const activeFeatures = countries.features.filter(feature =>
          feature.properties['ISO3166-1-Alpha-2'] === this.activeCountry
        )
        this.oceanMask = this.buildOceanMask(activeFeatures)
        if (this.oceanMask) this.oceanMask.addTo(this.map)
      }

      this.countryLayer = L.geoJSON(countries, {
        style: this.countryStyle.bind(this),
        onEachFeature: this.onEachCountryFeature.bind(this),
      }).addTo(this.map)

      this.fitBoundsToSelectedCountries(countries)

      if (this.activeCountry) {
        this.addTileLayer()
        this.addArticleMarkers()
      }
    } catch (error) {
      console.error("Unable to highlight countries", error)
    }
  }

  fitBoundsToSelectedCountries(countries) {
    if (this.countryCodes.length === 0) return

    let selectedFeatures

    // If we have an active country, fit to just that country
    if (this.activeCountry) {
      selectedFeatures = countries.features.filter(feature =>
        feature.properties['ISO3166-1-Alpha-2'] === this.activeCountry
      )
    } else {
      // Otherwise fit to all countries with articles
      selectedFeatures = countries.features.filter(feature =>
        this.countryCodes.includes(feature.properties['ISO3166-1-Alpha-2'])
      )
    }

    if (selectedFeatures.length > 0) {
      const selectedLayer = L.geoJSON({ type: "FeatureCollection", features: selectedFeatures })
      this.map.fitBounds(selectedLayer.getBounds(), {
        padding: [20, 20],
        animate: false
      })
    }
  }

  countryStyle(feature) {
    const countryCode = feature.properties['ISO3166-1-Alpha-2']

    // Active country - transparent to show map tiles beneath
    if (this.activeCountry && countryCode === this.activeCountry) {
      return {
        color: "#aaa",
        weight: 2,
        fillOpacity: 0,
      }
    }

    // Countries with articles (but not active)
    if (this.countryCodes.includes(countryCode)) {
      return {
        color: "#fff",
        weight: 1,
        fillColor: "#024e79",
        fillOpacity: 1,
      }
    }

    // Inactive countries - greyed out
    return {
      color: "#aaa",
      weight: 1,
      fillColor: "#aaa",
      fillOpacity: 1,
    }
  }

  highlightCountry(event) {
    const layer = event.target
    const countryCode = layer.feature.properties['ISO3166-1-Alpha-2']

    // Don't change style for active country
    if (this.activeCountry && countryCode === this.activeCountry) return

    layer.setStyle({
      weight: 2,
      color: "#fff",
      fillColor: "#ff4e00",
      fillOpacity: 1,
    })

    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      layer.bringToFront()
    }
  }

  resetCountryHighlight(event) {
    if (this.countryLayer) {
      this.countryLayer.resetStyle(event.target)
    }
  }

  async onCountryClick(event) {
    const layer = event.target
    const countryCode = layer.feature.properties['ISO3166-1-Alpha-2']

    // Only proceed for countries with articles
    if (!this.countryCodes.includes(countryCode)) {
      this.map.fitBounds(layer.getBounds())
      return
    }

    const countrySlug = this.getCountrySlug(countryCode)

    // Navigate to the country page using Turbo
    Turbo.visit(`/${countrySlug}`)
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.remove())
    this.markers = []
  }

  showResetButton() {
    if (this.resetButton) return

    this.resetButton = document.createElement('button')
    this.resetButton.innerHTML = '&times;'
    this.resetButton.className = 'map-reset-button'
    this.resetButton.setAttribute('title', 'Zobrazit všechny země')
    this.resetButton.addEventListener('click', (e) => {
      e.stopPropagation()
      this.resetView()
    })
    this.element.appendChild(this.resetButton)
  }

  hideResetButton() {
    if (this.resetButton) {
      this.resetButton.remove()
      this.resetButton = null
    }
  }

  async resetView(event) {
    if (event) event.preventDefault()

    // Navigate to home using Turbo
    Turbo.visit('/')
  }

  updateCountrySelect(countrySlug) {
    const select = document.getElementById('country-select')
    if (select) {
      select.value = countrySlug || ''
    }

    const resetBtn = document.getElementById('reset-filters-btn')
    if (resetBtn) {
      resetBtn.style.display = countrySlug ? '' : 'none'
    }
  }

  onEachCountryFeature(feature, layer) {
    const countryCode = feature.properties['ISO3166-1-Alpha-2']
    const hasArticles = this.countryCodes.includes(countryCode)

    if (hasArticles) {
      const countryName = this.countryNames[countryCode] || feature.properties.name

      layer.on({
        mouseover: this.highlightCountry,
        mouseout: this.resetCountryHighlight,
        click: this.onCountryClick,
      })

      if (countryName) {
        layer.bindTooltip(countryName, { sticky: true })
      }
    }
  }

  addArticleMarkers() {
    this.articles.slice().reverse().forEach(article => {
      const pinUrl = article.pin || this.pinPath
      const pinIcon = L.icon({
        iconUrl: pinUrl,
        iconAnchor: [24, 64],
        popupAnchor: [0, -64]
      })

      const marker = L.marker([article.lat, article.lon], { icon: pinIcon }).addTo(this.map)
      this.markers.push(marker)

      const imageHtml = article.image
        ? `<a href="${article.url}"><img src="${article.image}" style="width: 200px; height: 120px; object-fit: cover; border-radius: 8px; margin-bottom: 8px;"></a>`
        : ''

      const popupContent = `
        <div style="text-align: center; min-width: 200px;">
          ${imageHtml}
          <div>
            <a href="${article.url}" style="color: #024e79; font-weight: bold; text-decoration: none; font-size: 14px;">
              ${article.title}
            </a>
          </div>
        </div>
      `

      marker.bindPopup(popupContent, {
        maxWidth: 250,
        className: 'article-popup'
      })

      if (article.route) {
        const routeLayer = L.geoJSON(article.route, {
          style: { color: '#ff4e00', weight: 4, opacity: 0.9 }
        }).addTo(this.map)
        routeLayer.on('click', () => Turbo.visit(article.url))
        routeLayer.bindPopup(popupContent, { maxWidth: 250, className: 'article-popup' })
        this.markers.push(routeLayer)
      }
    })
  }
}
